import React, { useEffect, useRef, useState } from "react";
import { clientConfig } from "../../../config";
import VerifiedIcon from "../../../icons/wl-verified.svg";
import * as translator from "../../../utils/translator";
import type { Gender } from "./navigationHelpers";
import { useShellId } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

// Four before/after photos exist under real-results/; photo5..photo10 were never uploaded.
export const PHOTOS = [1, 2, 3, 4].map((n) =>
  `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/real-results/photo${n}/photo${n}.jpg`
);

interface Testimonial {
  photoIndex: number;
  gender: Gender;
  nameAgeKey: string;
  badgeKey: string;
  monthEndKey: string;
  quoteKey: string;
}

/**
 * One card per photo we actually have, and each photo used exactly once.
 *
 * This rail used to render all ten translated testimonials over the four photos by cycling
 * `photoIndex` (0,1,2,3,0,1,...), which put the same face behind two or three different names and
 * ages — Frank, 60 on the ~30-year-old woman, Sabine, 48 on a man — and made the whole section read
 * as invented. Ten identities backed by four stock before/afters is also not a claim we can stand
 * behind on a medical funnel.
 *
 * The indices below look shuffled because photo2 and photo4 are transposed relative to the copy:
 * photo2 is a woman of about 55 and photo4 a man of about 38, so they belong to Monika, 52 and
 * Tobias, 44 respectively — not the other way round. Pairing by hand rather than by position is
 * what keeps that honest, so keep it explicit even though 1 and 3 sit at their natural index.
 *
 * Testimonials 5-10 stay in the translation files, unused. When marketing supplies photo5..photo10,
 * extend PHOTOS and add their rows back here, one photo each.
 */
export const TESTIMONIALS: Testimonial[] = [
  { photoIndex: 0, gender: "female", nameAgeKey: "real_results_testimonial1_name_age", badgeKey: "real_results_testimonial1_badge", monthEndKey: "real_results_testimonial1_month_end", quoteKey: "real_results_testimonial1_quote" },
  { photoIndex: 3, gender: "male", nameAgeKey: "real_results_testimonial2_name_age", badgeKey: "real_results_testimonial2_badge", monthEndKey: "real_results_testimonial2_month_end", quoteKey: "real_results_testimonial2_quote" },
  { photoIndex: 2, gender: "male", nameAgeKey: "real_results_testimonial3_name_age", badgeKey: "real_results_testimonial3_badge", monthEndKey: "real_results_testimonial3_month_end", quoteKey: "real_results_testimonial3_quote" },
  { photoIndex: 1, gender: "female", nameAgeKey: "real_results_testimonial4_name_age", badgeKey: "real_results_testimonial4_badge", monthEndKey: "real_results_testimonial4_month_end", quoteKey: "real_results_testimonial4_quote" },
];

// HL-12282: the patient's own gender first, all four still shown. Array.sort is stable.
export const orderTestimonials = (gender?: Gender): Testimonial[] =>
  gender ? [...TESTIMONIALS].sort((a, b) => Number(b.gender === gender) - Number(a.gender === gender)) : TESTIMONIALS;

const Card: React.FC<{ tst: Testimonial; language?: string }> = ({ tst, language }) => (
  <div data-card className="shrink-0 snap-start w-[318px] lg:w-[270px] rounded-[18px] lg:rounded-[15px] bg-[#f5f6f7] p-3 flex flex-col gap-[13px] lg:gap-[11px]">
    <div className="relative pt-3.5 lg:pt-3 w-[303px] lg:w-[257px]">
      <div className="w-[303px] lg:w-[257px] h-[206px] lg:h-[174px] rounded-[13px] lg:rounded-[11px] overflow-hidden">
        <img src={PHOTOS[tst.photoIndex]} alt="" className="w-full h-full object-cover object-top" />
      </div>
      <div
        className="absolute -top-1 left-1/4 -translate-x-1/4 min-w-[154px] lg:min-w-[130px] rounded-full px-3 py-1 text-white text-[13px] italic font-semibold font-['Poppins'] text-center whitespace-nowrap"
        style={{ backgroundImage: "linear-gradient(90deg, #f59402, #efcc3f)" }}
      >
        {t(language, tst.badgeKey)}
      </div>
    </div>

    <div className="flex items-center justify-between px-1">
      <span className="text-[13px] font-['Poppins']" style={{ color: "rgba(8,8,10,0.7)" }}>
        {t(language, "real_results_month_zero_label")}
      </span>
      <span className="text-[13px] font-['Poppins']" style={{ color: "rgba(8,8,10,0.7)" }}>
        {t(language, tst.monthEndKey)}
      </span>
    </div>

    <p className="italic text-[14px] text-[#08080a] font-['Poppins']">{t(language, tst.quoteKey)}</p>
    <p className="text-center text-[14px] font-medium text-[#08080a] font-['Poppins']">{t(language, tst.nameAgeKey)}</p>
    <div className="self-center inline-flex items-center gap-1 bg-[#dee5f3] rounded-full px-3 py-1 w-fit">
      <img src={VerifiedIcon} alt="" className="size-[14px]" />
      <span className="text-[12px] text-[#4266B3] font-['Poppins']">{t(language, "real_results_verified_badge_label")}</span>
    </div>
  </div>
);

const PrevButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    id={useShellId("wl2_real_results_prev")}
    aria-label="Previous"
    onClick={onClick}
    className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shrink-0 cursor-pointer active:scale-95 transition"
    style={{ border: "none", boxShadow: "0px -12px 16px 0px rgba(255,255,255,0.82), 0px 12px 18px 0px rgba(206,208,216,0.7)" }}
  >
    <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
      <path d="M17 8H1m0 0l7-7M1 8l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const NextButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    id={useShellId("wl2_real_results_next")}
    aria-label="Next"
    onClick={onClick}
    className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shrink-0 cursor-pointer active:scale-95 transition"
    style={{ border: "none", boxShadow: "0px -12px 16px 0px rgba(255,255,255,0.82), 0px 12px 18px 0px rgba(206,208,216,0.7)" }}
  >
    <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
      <path d="M1 8h16m0 0l-7-7m7 7l-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const Dots: React.FC<{ active: number }> = ({ active }) => (
  <div className="lg:hidden flex items-center gap-2">
    {[0, 1, 2].map((dot) => (
      <span
        key={dot}
        className="rounded-full transition-all"
        style={
          dot === active
            ? { width: 20, height: 8, backgroundImage: "linear-gradient(90deg, #f59402, #efcc3f)" }
            : { width: 8, height: 8, backgroundColor: "#d0d5dd" }
        }
      />
    ))}
  </div>
);

interface RealResultsPageProps {
  language?: string;
  gender?: Gender;
}

const RealResultsPage: React.FC<RealResultsPageProps> = ({ language, gender }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mobileActiveDot, setMobileActiveDot] = useState(0);
  // Four cards no longer overflow every viewport the way ten did. Where they fit, the arrows have
  // nothing to scroll and clicking them does nothing (scrollByCard bails on a sub-pixel delta), so
  // measure the rail and hide the controls rather than leave dead buttons on the page.
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Card and spacer widths are fixed in CSS, so the rail's scrollWidth does not wait on the
    // photos loading — only on the container being resized around it.
    const measure = () => setCanScroll(el.scrollWidth - el.clientWidth > 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]") as HTMLElement | null;
    const cardWidth = card?.offsetWidth ?? 270;
    const row = card?.parentElement;
    const gap = row
      ? parseFloat(getComputedStyle(row).columnGap || getComputedStyle(row).gap) || 11
      : 11;
    const step = cardWidth + gap;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    let target = el.scrollLeft + dir * step;

    // Absorb leftover end/start slack so the last click finishes the rail
    // instead of a tiny nudge into the edge spacer.
    if (dir === 1 && maxScroll - target < step) target = maxScroll;
    if (dir === -1 && target < step) target = 0;

    target = Math.min(maxScroll, Math.max(0, target));
    if (Math.abs(target - el.scrollLeft) < 1) return;
    el.scrollTo({ left: target, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col gap-4 lg:-mx-16">
      <div
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll <= 0) return;
          setMobileActiveDot(Math.min(2, Math.max(0, Math.round((el.scrollLeft / maxScroll) * 2))));
        }}
        className="flex overflow-x-auto snap-x snap-mandatory lg:snap-proximity scroll-smooth touch-pan-x scroll-px-6 lg:scroll-px-[75px] [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        <div aria-hidden className="shrink-0 w-6 lg:w-[75px]" />
        <div className="flex shrink-0 gap-[13px] lg:gap-[11px]">
          {orderTestimonials(gender).map((tst) => (
            <Card key={tst.nameAgeKey} tst={tst} language={language} />
          ))}
        </div>
        <div aria-hidden className="shrink-0 w-6 lg:w-[75px]" />
      </div>

      {canScroll && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <PrevButton onClick={() => scrollByCard(-1)} />
          <Dots active={mobileActiveDot} />
          <NextButton onClick={() => scrollByCard(1)} />
        </div>
      )}
    </div>
  );
};

export default RealResultsPage;